import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { get, uniqueId } from 'lodash';
import { SdfLink } from '@synerg/react-components';
import { ProjectTitle } from './project-details/projectTitle';
import { ProjectHeader } from './project-details/projectHeader';
import PartnerProjectSteps from './project-details/partnerProjectSteps';
import {
  ApplicationStateContext,
  useApplicationState,
  updateCurrentProjectDetails,
  updateCurrentDevProjectDetails,
  updateCurrentProdProjectDetails,
  setLoadings,
  updateCurrentProjectDevSubscriptions,
  updateCurrentProjectProdSubscriptions,
  setSubscriptionLoadings
} from '../../utilities/applicationState';
import Toast from '../common/toast';
import { SdfIcon } from '@synerg/react-components';
import { withConfig } from '../../utilities/appConfig';

const ProjectDetail = ({ config }) => {
  const history = useHistory();
  const projectListData = history.location.state;
  const [store, dispatch] = useApplicationState();
  const { projects: { current } } = store;
  const { devInfo, prodInfo, appListingSubmitStatus = [], appListingUrlNumber = [], appListingUrls = [] } = current;
  const [toast, setToast] = useState(false);

  // BASIC STATES
  const [cancelableRequest, setCancelableRequest] = useState(false);
  const [updateProjectDetails, setUpdateProjectDetails] = useState(null);
  const emptyUrlFields = [{ nameId: 'listing-name-0', urlId: 'listing-url-0' }];

  // Webhook states - DEV
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookError, setWebhookError] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(true);

  // Webhook states - PROD
  const [prodWebhookConfig, setProdWebhookConfig] = useState(null);
  const [prodWebhookError, setProdWebhookError] = useState(null);
  const [prodWebhookLoading, setProdWebhookLoading] = useState(true);

  // Check if webhooks feature flag is enabled
  const webhooksEnabled = config?.['webhookfeatureflag']?.enabled;

  const handleResponse = (message, status = "error") => {
    setToast({ key: uniqueId(), status: status, message: message, showToast: true });
  };

  // LOADING ACTIONS
  const setLoadingState = () => {
    dispatch(setLoadings({
      loadingProjectInfo: true,
      loadingClientCredentials: true,
      loadingProjectDetails: true,
      loadingDevClientCredentials: true,
      loadingAuthorizationCode: true,
      loadingCertificate: true
    }));
  };

  const setUnloadedState = () => {
    setUnloadedClientCredentials();
    setUnloadedAuthorizationCode();
    setUnloadedCertificate();
    setUnloadedDetails();
    setProdUnloadedDetails();
    // Stop loading animation

    // update app state for components that need this
    dispatch(updateCurrentProjectDetails({
      devInfo: {
        name: 'Not available',
        orgName: 'Not available',
        startDate: 'Not available',
        estimatedEndDate: 'Not available',
        contacts: [],
        securityMasters: [],
        milestones: []
      }
    }));
    dispatch(setLoadings({
      loadingProjectInfo: false,
      loadingClientCredentials: false,
      loadingProjectDetails: false,
      loadingDevClientCredentials: false,
      loadingAuthorizationCode: false,
      loadingCertificate: false
    }));
  };

  const setUnloadedClientCredentials = () => {
    dispatch(updateCurrentProjectDetails({
      b2bClientId: 'Not available',
      b2bClientSecret: null,
      prodb2bClientId: 'Not available',
      prodb2bClientSecret: null
    }));

    dispatch(setLoadings({ loadingClientCredentials: false, loadingDevClientCredentials: false }));
  };

  const setUnloadedAuthorizationCode = () => {
    dispatch(updateCurrentProjectDetails({
      b2cClientId: 'Not available',
      b2cClientSecret: null,
      prodb2cClientId: 'Not available',
      prodb2cClientSecret: null
    }));

    dispatch(setLoadings({ loadingAuthorizationCode: false }));
  };

  const setUnloadedCertificate = () => {
    const cert = {
      alertType: 'warning',
      remainingTime: 'No certificate has been uploaded',
      certificateName: 'Not available',
      expiresOn: 'Not available',
      uploadedOn: 'Not available'
    };

    dispatch(updateCurrentProjectDetails({ allCertificates: [cert] }));

    dispatch(setLoadings({ loadingCertificate: false }));
  };

  const setUnloadedDetails = () => {
    dispatch(updateCurrentProjectDetails({ devProgress: [], errorLoadingProject: true }));
    dispatch(setLoadings({ loadingProjectDetails: false }));
  };

  const setProdUnloadedDetails = () => {
    dispatch(updateCurrentProjectDetails({ prodProgress: [], errorLoadingProdProject: true }));
    dispatch(setLoadings({ loadingProjectDetails: false }));
  };

  // DATA OPERATIONS
  const getContacts = async () => {
    try {
      const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${devInfo.ooid}`);
      const contacts = Array.isArray(data.contacts) ? data.contacts : [];
      dispatch(updateCurrentDevProjectDetails({ contacts, orgName: data.orgName, isiOrgCd: data.isiOrgCd }));

      // Merge isiOrgCd into projectListData
      history.replace({
        ...history.location,
        state: {
          ...projectListData,
          isiOrgCd: data.isiOrgCd
        }
      });
    } catch (e) {
      dispatch(updateCurrentDevProjectDetails({ contacts: [], orgName: 'Not available' }));
    }
  };

  const getSecurityMasters = async () => {
    try {
      const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/partner/projects/security-masters/${devInfo.ooid}`);
      const securityMasters = data.map(({ displayName, email }) => ({ name: displayName, email }));
      dispatch(updateCurrentDevProjectDetails({ securityMasters }))

    } catch (e) {
      dispatch(updateCurrentDevProjectDetails({ securityMasters: [] }))
    }
  };

  const getClientId = async (consumerApplicationID, projectID) => {
    const { data: { clientID } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-id`, {
      params: {
        projectID,
        consumerApplicationID
      }
    });
    return clientID;
  };

  const getClientSecret = async (consumerApplicationID, projectID) => {
    const { data: { clientSecret } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-secret`, {
      params: {
        projectID,
        consumerApplicationID
      }
    });
    return clientSecret;
  };

  //CORE METHODS

  const getProjectDetails = async () => {
    try {
      setLoadingState();
      const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/admin-view`, {
        params: {
          projectID: (projectListData.type === 'PROD' && !projectListData.legacy) ? projectListData.link : projectListData.projectAppId,
          displayData: true
        }
      });
      const legacyCaseDisplay = projectListData.legacy ? 'Legacy Project' : undefined;

      if (projectListData.legacy) {
        const legacyInfo = {
          ...data,
          legacyCaseDisplay,
          consumerApplicationID: data.b2bConsumerAppID || data.b2cConsumerAppID
        };

        if (Array.isArray(legacyInfo.appListingUrls) && legacyInfo.appListingUrls.length > 0) {
          fillAppListingFields(legacyInfo.appListingUrls);
          dispatch(updateCurrentProjectDetails({ isAppListingEditMode: false }));

        } else {
          fillAppListingFields(emptyUrlFields);
          dispatch(updateCurrentProjectDetails({ isAppListingEditMode: true }));
        }

        dispatch(updateCurrentProjectDetails({ appListingSubmitStatus: null }));

        // update app state for components who need this
        dispatch(updateCurrentDevProjectDetails({ ...legacyInfo, type: legacyInfo.type || 'DEV' }));
        dispatch(setLoadings({ loadingProjectDetails: false }));
        setUpdateProjectDetails(new Date());
        if (legacyInfo.type === 'PROD') {
          dispatch(updateCurrentProdProjectDetails({ ...legacyInfo, type: 'PROD' }));
          return;
        }
      }

      let progress = 'In Progress';

      if (data.link) {
        progress = 'Finished';
      }

      if (data.associatedProject) {
        const prodProject = data.associatedProject;

        if (Array.isArray(prodProject.appListingUrls) && prodProject.appListingUrls.length > 0) {
          fillAppListingFields(prodProject.appListingUrls);
          dispatch(updateCurrentProjectDetails({ isAppListingEditMode: false }));

        } else {
          fillAppListingFields(emptyUrlFields);
          dispatch(updateCurrentProjectDetails({ isAppListingEditMode: true }));
        }

        dispatch(updateCurrentProdProjectDetails({
          ...prodProject,
          consumerApplicationID: prodProject.b2bConsumerAppID || prodProject.b2cConsumerAppID
        }));
      }

      // update prodInfo, devInfo  in application state for components who need this
      dispatch(updateCurrentProjectDetails({
        devInfo: {
          ...data,
          progress,
          legacyCaseDisplay,
          consumerApplicationID: data.b2bConsumerAppID || data.b2cConsumerAppID
        }
      }));

      dispatch(setLoadings({ loadingProjectDetails: false }));
      setUpdateProjectDetails(new Date());
    } catch (e) {
      setUnloadedState();
      if (e && e.message !== "search request canceled") {
        /* eslint-disable no-unused-vars */
        const errorMessage = get(e, "response.data.message",
          "Something went wrong when trying to get Project List. Please try again."
        );
        /* eslint-disable no-unused-vars */
      }
    }
  };


  const getProjectInformation = () => {
    Promise.all([getContacts(), getSecurityMasters()]).then(() => {
      dispatch(setLoadings({ loadingProjectInfo: false }));
    });
  };

  const getDevProgress = () => {
    if (!Array.isArray(devInfo.milestones)) {
      return setUnloadedDetails();
    }

    const findKeyInfo = (history, key) => {
      return history ? history.find(el => el.key === key) : {};
    };

    const progress = devInfo.milestones.map(item => ({
      title: item.name,
      history: item.history,
      startDateInfo: findKeyInfo(item.history, 'STARTDATE'),
      endDateInfo: findKeyInfo(item.history, 'ENDDATE'),
      scopeInfo: findKeyInfo(item.history, 'SCOPE'),
      completeInfo: findKeyInfo(item.history, 'COMPLETE')
    }));

    dispatch(updateCurrentProjectDetails({ devProgress: progress, errorLoadingProject: false }));
    dispatch(setLoadings({ loadingProjectDetails: false }));
  };

  const getProdProgress = () => {
    if (!Array.isArray(prodInfo.milestones)) {
      return setProdUnloadedDetails();
    }

    const getRow = (row) => ({
      isDone: Boolean(row.actualEndDate),
      text: row.name,
      linkSrc: row.documentationURI,
      linkName: row.documentationName || 'Unnamed Document',
      duration: row.duration,
      actualFinishDate: row.actualEndDate,
      suggestedFinishDate: row.estimatedEndDate
    });

    const progress = prodInfo.milestones.map(item => ({
      title: item.name,
      actualFinishDate: item.actualEndDate,
      suggestedFinishDate: item.estimatedEndDate,
      rows: !item.objectives ? [] : item.objectives.map(objective => ({
        ...getRow(objective),
        rows: !objective.tasks ? [] : objective.tasks.map(getRow)
      }))
    }));

    dispatch(updateCurrentProjectDetails({ prodProgress: progress, errorLoadingProject: false }));
    dispatch(setLoadings({ loadingProjectDetails: false }));
  };

  const getDevCredentials = () => {
    /// clear any previous dev credentials
    dispatch(updateCurrentProjectDetails({ b2bClientId: 'Not available', b2cClientId: 'Not available' }));

    // Client Credentials
    if (devInfo.b2bConsumerAppID && devInfo._id) {
      getClientId(devInfo.b2bConsumerAppID, devInfo._id).then(clientId => {
        dispatch(updateCurrentProjectDetails({ b2bClientId: clientId }));
        dispatch(setLoadings({ loadingDevClientCredentials: false }));
      }).catch(() => setUnloadedClientCredentials());
    }

    // Authorization Code
    if (devInfo.b2cConsumerAppID && devInfo._id) {
      getClientId(devInfo.b2cConsumerAppID, devInfo._id).then(clientId => {
        dispatch(updateCurrentProjectDetails({ b2cClientId: clientId }));
        dispatch(setLoadings({ loadingAuthorizationCode: false }));
      }).catch(() => setUnloadedAuthorizationCode());
    }
  };

  const getProdCredentials = () => {
    // clear any previous prod credentials
    dispatch(updateCurrentProjectDetails({ prodb2bClientId: '', prodb2cClientId: '' }));

    // Client Credentials
    if (prodInfo.b2bConsumerAppID && prodInfo._id) {
      getClientId(prodInfo.b2bConsumerAppID, prodInfo._id).then(clientId => {
        dispatch(updateCurrentProjectDetails({ prodb2bClientId: clientId }));
        dispatch(setLoadings({ loadingClientCredentials: false }));
      }).catch(() => setUnloadedClientCredentials());
    }

    // Authorization Code
    if (prodInfo.b2cConsumerAppID && prodInfo._id) {
      getClientId(prodInfo.b2cConsumerAppID, prodInfo._id).then(clientId => {
        dispatch(updateCurrentProjectDetails({ prodb2cClientId: clientId }));
        dispatch(setLoadings({ loadingAuthorizationCode: false }));
      }).catch(() => setUnloadedAuthorizationCode());
    }
  };

  const getCertificateExpiryInfo = (certificate = {}) => {
    const expiryInfo = { expiryMessage: 'Certificate expiry date unavailable', alertType: 'warning' };
    const expiresIn = get(certificate, 'expiresIn');
    if (expiresIn === 'N/A') {
      expiryInfo.expiryMessage = 'Generation Failed.';
      expiryInfo.alertType = 'error';
    } else if (expiresIn > 0) {
      expiryInfo.expiryMessage = `${expiresIn} days remain before certificate expires.`;
    } else if (expiresIn < 0) {
      expiryInfo.alertType = 'error';
      expiryInfo.expiryMessage = `Your certificate expired ${-expiresIn} days ago. Please request a new certificate.`;
    } else if (expiresIn === 0) {
      expiryInfo.alertType = 'error';
      expiryInfo.expiryMessage = 'Your certificate expired today. Please request a new certificate.';
    }
    return expiryInfo;
  };

  const getCertificate = async () => {
    try {
      const { data } = await axios(`${import.meta.env.VITE_ROOT_PATH}/api/partner/${devInfo.ooid}/certificates`);

      const allCertificatesAux = [];
      for (let cert of data.allCertificatesInfo) {
        if (cert !== null) {
          const expiryInfoAux = getCertificateExpiryInfo(cert);
          let certificateAux;
          if (cert.status === undefined) {
            certificateAux = {
              certificate: cert.certificate,
              alertType: expiryInfoAux.alertType,
              remainingTime: expiryInfoAux.expiryMessage,
              certificateName: cert.name,
              expiresOn: cert.expiresAt,
              uploadedOn: cert.uploadedOn,
              isSuccess: true
            };
          } else if (cert.status === 'failed') {
            certificateAux = {
              certificate: '',
              alertType: expiryInfoAux.alertType,
              remainingTime: expiryInfoAux.expiryMessage,
              certificateName: cert.commonName,
              expiresOn: cert.expiresIn,
              uploadedOn: cert.createdAt,
              isSuccess: false
            }
          }
          allCertificatesAux.push(certificateAux);
        }
      }

      if (allCertificatesAux.length > 0) {
        // update app state for components that need this
        dispatch(updateCurrentProjectDetails({ allCertificates: allCertificatesAux }));
      } else {
        return setUnloadedCertificate();
      }

      dispatch(setLoadings({ loadingCertificate: false }));

    } catch (e) {
      setUnloadedCertificate();
    }
  };

  const getCancelableRequestToken = () => {
    if (cancelableRequest) {
      cancelableRequest.cancel('search request canceled');
    }
    const newRequest = axios.CancelToken.source();
    setCancelableRequest(newRequest);
    return newRequest;
  };

  const getCarDetails = async (opts = {}) => {
    try {
      if (opts.type) {
        let obj = {};
        obj[opts.type] = true;
        dispatch(setSubscriptionLoadings({ isLoadingSubscriptions: obj }));
      } else {
        dispatch(updateCurrentProjectDetails({ projectCarDetails: {} }));
        dispatch(setLoadings({ isLoadingCarDetails: true }));
      }

      const paramsAppDetails = {
        removeOwnOrgSubs: true,
        projectType: opts.projectType,
        ...(opts.search && { search: opts.search }),
        ...(opts.subscriptions && { subscriptions: opts.subscriptions }),
        ...(opts.consents && { consents: opts.consents }),
        type: opts.type || 'all'
      };
      const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/car-app-details/${projectListData.projectAppId}`, {
        paramsAppDetails,
        cancelToken: getCancelableRequestToken().token
      });
      if (opts.type) {
        if (opts.projectType === 'PROD') {
          dispatch(updateCurrentProjectProdSubscriptions({ ...data.prod.subscriptions }));
        } else {
          dispatch(updateCurrentProjectDevSubscriptions({ ...data.subscriptions }));
        }
        dispatch(setSubscriptionLoadings({ isLoadingSubscriptions: {} }));
      } else {
        dispatch(updateCurrentProjectDetails({ projectCarDetails: { ...data } }));
        dispatch(setLoadings({ isLoadingCarDetails: false }));
      }

    } catch (e) {
      dispatch(setLoadings({ isLoadingCarDetails: false }));
      if (e && e.message !== 'search request canceled') {
        handleResponse(get(e, 'response.data.message', 'Something went wrong when trying to get CAR App details. Please try again.'));
      }
    }
  };

  const fillAppListingFields = fields => {
    const clean = str => (str || '').replace(/[^a-z0-9]/gi, '').toLowerCase();
    if (Array.isArray(fields) && fields.length > 0) {
      const list = fields.map(url => {
        const newFieldId = Math.random().toString(36).substring(2);
        return {
          urlId: `listing-url-${clean(url.url + url.name)}-${newFieldId}`,
          url: url.url,
          nameId: `listing-name-${clean(url.url + url.name)}-${newFieldId}`,
          name: url.name
        };
      });
      dispatch(updateCurrentProjectDetails({ appListingUrls: list, appListingUrlNumber: list.length }));
    }
  };

  const submitAppListingFields = async (urls) => {
    try {
      dispatch(setLoadings({ isLoadingAppListing: true }));
      const urlList = Object.keys(urls).reduce((list, url) => {
        const fieldId = url.split('-').pop();
        const fieldValue = urls[url];
        let found = list.find(({ id }) => id === fieldId);
        if (!found) {
          found = { id: fieldId };
          list.push(found);
        }
        found[url.startsWith('listing-name') ? 'name' : 'url'] = fieldValue;
        return list;
      }, []);

      await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/app-store-urls`, {
        urls: urlList,
        id: prodInfo._id
      });

      fillAppListingFields(urlList);

      // update app state for components that need this
      dispatch(updateCurrentProdProjectDetails({
        appListingUrls: urlList.map(({ name, url }) => ({ name, url }))
      }));

      dispatch(updateCurrentProjectDetails({
        appListingSubmitStatus: 'success',
        appListingUrlNumber: Math.abs(urlList.length - appListingUrlNumber),
        isAppListingEditMode: false
      }));

      dispatch(setLoadings({ isLoadingAppListing: false }));

    } catch (e) {
      dispatch(updateCurrentProjectDetails({ appListingSubmitStatus: 'error' }));

      dispatch(setLoadings({ isLoadingAppListing: false }));
    }
  };

  const redirectToHome = () => {
    dispatch(updateCurrentProjectDetails({ devInfo: { contacts: [], securityMasters: [] } }));
    history.push(`/partner-projects/`);
  };

  // Fetch webhook configuration for a specific environment
  const fetchWebhookConfig = async (consumerApplicationID, isProd = false) => {
    const setLoading = isProd ? setProdWebhookLoading : setWebhookLoading;
    const setError = isProd ? setProdWebhookError : setWebhookError;
    const setConfig = isProd ? setProdWebhookConfig : setWebhookConfig;

    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_ROOT_PATH}/api/project/webhook-configuration?consumerApplicationID=${consumerApplicationID}`
      );

      const data = Array.isArray(res.data) ? res.data[0] : res.data;
            
      if (data && Object.keys(data).length > 0) {
        setConfig(data);
      } else {
        setConfig(null);
      }
    } catch (err) {
      setError('Failed to load webhook configuration');
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  //HOOKS
  useEffect(() => {
    getProjectDetails();
    dispatch(updateCurrentProjectDetails({
      showB2bSecret: false,
      showB2cSecret: false,
      showProdB2bSecret: false,
      showProdB2cSecret: false,
      b2bClientSecret: false,
      b2cClientSecret: false,
      prodb2bClientSecret: false,
      prodb2cClientSecret: false
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (updateProjectDetails) {
      getProjectInformation();
      getCarDetails();
      getDevCredentials();
      getCertificate();
      if (!projectListData.legacy) {
        getDevProgress();
      }
      setUpdateProjectDetails(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateProjectDetails]);

  useEffect(() => {
    if (updateProjectDetails && prodInfo.consumerApplicationID) {
      getProdCredentials();
      if (!projectListData.legacy) {
        getProdProgress();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateProjectDetails]);

  useEffect(() => {
    if (appListingSubmitStatus === 'success') {
      setTimeout(() => dispatch(updateCurrentProjectDetails({
        appListingSubmitStatus: null,
        appListingUrlNumber: appListingUrls.length
      })), 3000);
    }
    if (appListingSubmitStatus === 'error') {
      setTimeout(() => dispatch(updateCurrentProjectDetails({
        appListingSubmitStatus: null,
        appListingUrlNumber: appListingUrls.length
      })), 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appListingSubmitStatus]);

  // Fetch webhook configurations for both DEV and PROD (if exists)
  useEffect(() => {
    if (webhooksEnabled) {
      // Fetch DEV webhook config
      if (devInfo && devInfo.consumerApplicationID) {
        fetchWebhookConfig(devInfo.consumerApplicationID, false);
      } else {
        setWebhookLoading(false);
      }
      
      // Fetch PROD webhook config ONLY if prodInfo exists
      if (prodInfo && prodInfo.consumerApplicationID) {
        fetchWebhookConfig(prodInfo.consumerApplicationID, true);
      } else {
        setProdWebhookLoading(false);
      }
    } else {
      // If feature is disabled, set loading to false immediately
      setWebhookLoading(false);
      setProdWebhookLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devInfo?.consumerApplicationID, prodInfo?.consumerApplicationID, webhooksEnabled]);

  return (
    <ApplicationStateContext.Provider value={[store, dispatch]}>
      <div className="mx-24 my-10">
        {toast && <Toast
          key={toast.key}
          status={toast.status}
          message={toast.message}
          showToast={toast.showToast}
        />}

        <SdfLink className='cursor-pointer' slot='breadcrumb' size='lg' onClick={redirectToHome}>
          <SdfIcon icon='nav-move-left' className='hydrated' />
          Back to all projects
        </SdfLink>

        <ProjectTitle
          projectDetails={devInfo}
          projectListData={projectListData}
          handleResponse={handleResponse}
        />
        <ProjectHeader projectListData={projectListData} />

        <div className="mt-6">
          <div className="text-heading-06 font-black">Project Details</div>
          <div className="text-md mt-2">
            Make a selection to view credentials, project scope, subscriptions and more.
          </div>
          <PartnerProjectSteps
            projectListData={projectListData}
            getClientSecret={getClientSecret}
            projectDetails={devInfo}
            submitAppListingFields={submitAppListingFields}
            webhookConfig={webhookConfig}
            webhookError={webhookError}
            webhookLoading={webhookLoading}
            prodWebhookConfig={prodWebhookConfig}
            prodWebhookError={prodWebhookError}
            prodWebhookLoading={prodWebhookLoading}
          />
        </div>
      </div>
    </ApplicationStateContext.Provider>
  );
};

export default withConfig(ProjectDetail);
